import type {
  AgendaItem,
  ActionItem as StitchActionItem,
} from "@elation/api-client/src/stitch-web";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { cn } from "@src/shared/lib/utils";

import { ActionItem } from "./ActionItem";
import {
  ActionItemSection,
  sectionHeaderIconMap,
  sectionTheme,
} from "./ActionItems.types";
import { useActionItemMutations } from "./useActionItemMutations";

export interface ActionItemsSectionProps {
  actionItems: (StitchActionItem & { agendaItem: AgendaItem | null })[];
  section: ActionItemSection;
  encounterId: number;
}

export const ActionItemsSection = ({
  actionItems,
  section,
  encounterId,
}: ActionItemsSectionProps) => {
  const Icon = sectionHeaderIconMap[section];
  const theme = sectionTheme[section];
  // One mutation hook per section, shared across its items (all items here
  // belong to the same encounter / query key).
  const { toggleActionItem } = useActionItemMutations(encounterId);
  return (
    <VerticalFlexbox as="li" gap="xs">
      <HorizontalFlexbox alignItems="center" gap="xs">
        <Icon className={cn("size-4 shrink-0", theme.icon)} />
        <HeaderText tag="h5" fontWeight={600} className="text-sm">
          {section}{" "}
          <Text
            as="span"
            sizing="xs"
            fontWeight={500}
            appearance="subdued"
            className="font-normal normal-case tracking-normal"
          >
            [{actionItems.length}]
          </Text>
        </HeaderText>
      </HorizontalFlexbox>
      {actionItems.length > 0 ? (
        <VerticalFlexbox as="ul" gap="xs">
          {actionItems.map((actionItem) => (
            <ActionItem
              key={actionItem.id}
              actionItem={actionItem}
              section={section}
              agendaItem={actionItem.agendaItem}
              onToggle={toggleActionItem}
            />
          ))}
        </VerticalFlexbox>
      ) : null}
    </VerticalFlexbox>
  );
};
